import dayjs from 'dayjs';

import { RedemptionProvider } from '../../../features/payments/redemptions/enums';
import { TimePeriod } from '../../../features/payments/redemptions/limits/enums';
import type { LimitCheckResult } from '../../../features/payments/redemptions/limits/types';
import type { RedemptionCaptureResult } from '../../../stores/payredeem/types';

import RedeemRiddleDialog from './RedeemRiddleDialog';
import { type RedemptionStepsMapping } from './types';

const PERIOD_UI_MAPPING = {
  [TimePeriod.perRequest]: 'request',
  [TimePeriod.daily]: 'day',
  [TimePeriod.weekly]: 'week',
  [TimePeriod.monthly]: 'month',
};

const PROVIDER_UI_MAPPING: Record<RedemptionProvider, string> = {
  [RedemptionProvider.BANK]: 'IBT',
  [RedemptionProvider.CARD]: 'Credit Card',
  [RedemptionProvider.SKRILL]: 'Skrill',
  [RedemptionProvider.ACH]: 'ACH',
  [RedemptionProvider.PAYOUTS]: 'Prepaid',
  [RedemptionProvider.INTERAC]: 'INTERAC',
  [RedemptionProvider.SEPA]: 'SEPA',
  [RedemptionProvider.PAYSAFECARD]: 'PAYSAFECARD',
};

function formatPeriod(payload: LimitCheckResult<'notAllowed'>['payload']) {
  if (payload.period === TimePeriod.custom) {
    return `within ${dayjs(payload.start).format('YYYY-MM-DD HH:mm')} - ${dayjs(payload.end).format('YYYY-MM-DD HH:mm')}`;
  }

  return `per ${PERIOD_UI_MAPPING[payload.period]}`;
}

function formatDomain(payload: LimitCheckResult<'notAllowed'>['payload']) {
  switch (payload.domainKind) {
    case 'territory':
      return `in ${payload.domainName} territory`;
    case 'country':
      return `in ${payload.domainName}`;
    case 'provider':
      return `to ${PROVIDER_UI_MAPPING[payload.domainName]}`;
  }
}

export function formatRedemptionLimitMessage(checkResult: LimitCheckResult<'notAllowed'>) {
  const period = formatPeriod(checkResult.payload);
  const domain = formatDomain(checkResult.payload);

  return `The maximum value of any redemption ${domain} is $${checkResult.payload.amount} ${period}`;
}

export const getNextRedeemStep = (
  stepsMapping: RedemptionStepsMapping,
  redemptionCaptureResult: RedemptionCaptureResult | null,
) => {
  // `stepsMapping.initPoc === -1` means POC feature is off and the related components never made their way to wizard stack
  if (stepsMapping.initPoc === -1 || !redemptionCaptureResult) {
    return stepsMapping.success;
  }

  if (redemptionCaptureResult.preparationResult.status !== 'validityIssue') {
    // If the status is 'success', everything is OK.
    // 'statusMismatch' or 'failed' still mean the redemption request was created successfully. It's just
    //   a processing issue on our side that the user should not care about.
    return stepsMapping.success;
  }

  switch (redemptionCaptureResult.preparationResult.payload.status) {
    case 'failedToDetect':
      return stepsMapping.pocStuck;
    case 'kycRequired':
      return [stepsMapping.kycConfirm, stepsMapping.pocStuck].find((step) => step !== -1) ?? stepsMapping.failure;
    case 'pocRequired':
    case 'pocFailed':
      return stepsMapping.initPoc;
    case 'pocInProgress':
      return redemptionCaptureResult.preparationResult.payload.payload.hangingData
        ? stepsMapping.initPoc
        : stepsMapping.success;
  }
};

export const getExtraStepsByChallengeType = (redeemChallengeType: unknown) => {
  switch (redeemChallengeType) {
    case 'riddle':
      return [RedeemRiddleDialog];
    default:
      return [];
  }
};

export const maskPhoneNumber = (phoneNumber?: string, digitsToShow = 4) => {
  if (!phoneNumber) return '';

  const cleanPhone = phoneNumber.replace(/\+/g, '');
  const regex = new RegExp(`\\d(?=\\d{${digitsToShow}})`, 'g');
  return cleanPhone.replace(regex, '*');
};
